/**
 * Auth API client for the admin dashboard.
 * Same endpoints as storefront but separate file for build isolation.
 */

import type { AuthUser } from '@provendoro/shared/types'
import type { LoginInput } from '@provendoro/shared/validation'

interface AuthApiResponse<T> {
  data?: T
  error?: { message: string; code: string }
}

async function authFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<AuthApiResponse<T>> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  return response.json() as Promise<AuthApiResponse<T>>
}

/** Login with email/password */
export async function signIn(input: LoginInput): Promise<AuthApiResponse<{ user: AuthUser }>> {
  return authFetch<{ user: AuthUser }>('/api/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Logout */
export async function signOut(): Promise<AuthApiResponse<{ success: boolean }>> {
  return authFetch<{ success: boolean }>('/api/auth/sign-out', {
    method: 'POST',
  })
}

/** Get current session */
export async function getSessionUser(): Promise<AuthApiResponse<{ user: AuthUser | null; expiresAt?: number }>> {
  return authFetch<{ user: AuthUser | null; expiresAt?: number }>('/api/auth/session')
}
